import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useIsNarrow, gridCols } from './ui';
import { complianceTaskTitle } from './complianceTaskTitle';
import { blobErrorDetail } from './downloadFile';

const API = '/api';

const C = {
  overdue: '#A63A3A',
  pending: '#c9a84c',
  done:    '#2D4A3E',
  navy:    '#1a2744',
};

const STATUS_STYLE = {
  Complete: { backgroundColor: '#eaf3ee', color: '#2D4A3E' },
  Pending:  { backgroundColor: '#fdf8ee', color: '#7a4d00' },
  Overdue:  { backgroundColor: '#fdf5f5', color: '#7a1f1f' },
};

function KpiCard({ label, value, accent, sub, onClick, active }) {
  const [hovered, setHovered] = React.useState(false);
  return (
    <div
      style={{
        backgroundColor: '#fff', borderRadius: '10px', padding: '18px 20px',
        boxShadow: active ? '0 0 0 2px #c9a84c' : hovered ? '0 0 0 1.5px #c9a84c' : '0 1px 3px rgba(0,0,0,0.05)',
        borderTop: '0.5px solid #EEE', borderRight: '0.5px solid #EEE',
        borderBottom: '0.5px solid #EEE', borderLeft: `3px solid ${accent}`,
        transition: 'box-shadow 0.12s', cursor: 'pointer',
      }}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <p style={{ fontSize: '11px', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 8px 0', fontWeight: 600 }}>{label}</p>
      <p style={{ fontSize: '28px', fontWeight: 700, margin: '0 0 4px 0', color: accent }}>{value ?? '-'}</p>
      <p style={{ fontSize: '11px', color: '#aaa', margin: 0 }}>{sub}</p>
    </div>
  );
}

export default function ComplianceReport() {
  // COMPSECTION: the compliance onboarding task is stored with one English title and translated
  // for display, so the label follows the language of the officer reading this card.
  const { t } = useTranslation();
  const [summary,      setSummary]      = useState(null);
  const [byType,       setByType]       = useState([]);
  const [outstanding,  setOutstanding]  = useState([]);
  const [byEntity,     setByEntity]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [tab,          setTab]          = useState('overview');
  // CARDFILTER (confirmed COO decision): clicking a summary card filters the "Attestations by
  // type" list below IN PLACE, replacing the old onNavigate click handlers that sent the COO to
  // the dead Analytics skeleton (with a dead, never-read URL query-string argument) and ejected
  // the compliance role from the report back to its overview. Local state, so the behavior is
  // identical in both mounts (COO Dashboard and ComplianceView) by construction - the onNavigate
  // prop is gone entirely. Values: null (no filter) | 'Complete' | 'Overdue' | 'Pending', the
  // exact status strings the detail rows carry. Clicking the same card again toggles the filter
  // off; "Total attestations" always clears it. Card numbers come from SUMFIX's summary endpoint
  // and are never touched by filtering.
  const [statusFilter, setStatusFilter] = useState(null);
  const toggleFilter = (status) => setStatusFilter(f => (f === status ? null : status));
  const isNarrow = useIsNarrow();

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/compliance/report/summary`),
      axios.get(`${API}/compliance/report/attestations-by-type`),
      axios.get(`${API}/compliance/report/outstanding-tasks`),
      axios.get(`${API}/compliance/report/by-entity`),
    ])
      .then(([s, t, o, e]) => {
        setSummary(s.data);
        setByType(t.data);
        setOutstanding(o.data);
        setByEntity(e.data);
      })
      .catch(() => setError('Failed to load compliance report data.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ padding: '40px', color: '#aaa', fontSize: '14px' }}>Loading compliance report...</div>;
  if (error)   return <div style={{ padding: '40px', color: C.overdue, fontSize: '14px' }}>{error}</div>;

  return (
    <div style={{ margin: '-20px -24px', padding: '24px 24px 0', background: '#edecea' }}>
      <h2 style={{ fontSize: '16px', fontWeight: 600, color: C.navy, margin: '0 0 12px 0' }}>Compliance Report</h2>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', borderBottom: '1px solid #ddd' }}>
        {[['overview', 'Overview'], ['registers', 'Registers']].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            padding: '8px 16px', fontSize: '13px', fontWeight: tab === k ? 600 : 400,
            color: tab === k ? C.navy : '#888',
            borderBottom: tab === k ? `2px solid ${C.pending}` : '2px solid transparent',
            marginBottom: '-1px',
          }}>{label}</button>
        ))}
      </div>

      {tab === 'registers' && <RegistersView />}

      {tab === 'overview' && (<>
      {/* ROW 1 - 4 KPI cards */}
      <div style={{ display: 'grid', gridTemplateColumns: gridCols(isNarrow, 'repeat(4, 1fr)'), gap: '14px', marginBottom: '20px' }}>
        {/* SUBTEXTFIX: subtitles now describe what SUMFIX actually counts - the entries listed
            in "Attestations by type" below, by status - replacing wording left over from the
            removed Task-based counting ("compliance tasks overdue/pending") and the raw-table
            "submissions" phrasing. Text only; values and CARDFILTER clicks untouched. */}
        <KpiCard label="Total attestations" value={summary?.total_attestations} accent={C.navy}    sub="all attestation entries"  onClick={() => setStatusFilter(null)} />
        <KpiCard label="Completed"          value={summary?.completed}          accent={C.done}    sub="entries marked Complete"  onClick={() => toggleFilter('Complete')} active={statusFilter === 'Complete'} />
        <KpiCard label="Overdue"            value={summary?.overdue}            accent={C.overdue} sub="entries marked Overdue"   onClick={() => toggleFilter('Overdue')} active={statusFilter === 'Overdue'} />
        <KpiCard label="Pending"            value={summary?.pending}            accent={C.pending} sub="entries marked Pending"   onClick={() => toggleFilter('Pending')} active={statusFilter === 'Pending'} />
      </div>

      {/* ROW 2 - 2 columns */}
      <div style={{ display: 'grid', gridTemplateColumns: gridCols(isNarrow, '1fr 1fr'), gap: '16px', alignItems: 'start' }}>

        {/* LEFT - Attestations by type */}
        <div style={s.card}>
          <h3 style={s.cardTitle}>
            Attestations by type
            {statusFilter && (
              <span style={{ fontWeight: 400, fontSize: '12px', color: '#aaa', marginLeft: '8px' }}>
                showing {statusFilter === 'Complete' ? 'Completed' : statusFilter} only
                <button
                  onClick={() => setStatusFilter(null)}
                  style={{ background: 'none', border: 'none', color: C.pending, cursor: 'pointer', fontSize: '12px', fontWeight: 600, marginLeft: '6px', padding: 0 }}
                >
                  ✕ clear
                </button>
              </span>
            )}
          </h3>
          {byType.length === 0 ? (
            <p style={s.empty}>No attestation data</p>
          ) : byType.map(group => {
            // CARDFILTER: pure display filter over the rows the endpoint returned - the group
            // metadata (name, completed/total badge) and the underlying data are untouched.
            const shown = statusFilter
              ? group.employees.filter(emp => emp.status === statusFilter)
              : group.employees;
            return (
            <div key={group.type_name} style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <span style={{ fontSize: '12px', fontWeight: 700, color: C.navy }}>{group.type_name}</span>
                <span style={{ backgroundColor: '#eaf3ee', color: '#2D4A3E', borderRadius: '10px', padding: '1px 7px', fontSize: '11px', fontWeight: 600 }}>
                  {group.completed}/{group.total}
                </span>
              </div>
              {shown.length === 0 ? (
                <p style={{ ...s.empty, margin: '4px 0 0 0' }}>
                  {statusFilter ? `No ${statusFilter === 'Complete' ? 'completed' : statusFilter.toLowerCase()} entries` : 'No submissions'}
                </p>
              ) : shown.map(emp => (
                <div key={emp.id} style={{ display: 'flex', alignItems: 'center', padding: '7px 10px', marginBottom: '4px', backgroundColor: '#f9f8f6', borderRadius: '6px' }}>
                  <span style={{ flex: 1, fontWeight: 600, fontSize: '13px', color: C.navy }}>{emp.name}</span>
                  <span style={{ ...s.badge, ...STATUS_STYLE[emp.status] }}>{emp.status}</span>
                </div>
              ))}
            </div>
            );
          })}
        </div>

        {/* RIGHT - 2 stacked cards */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

          {/* Card A - Outstanding compliance tasks */}
          <div style={s.card}>
            <h3 style={s.cardTitle}>Outstanding compliance tasks</h3>
            {outstanding.length === 0 ? (
              <p style={s.empty}>No outstanding compliance tasks</p>
            ) : outstanding.map((item, i) => {
              const accent = item.is_overdue ? C.overdue : C.pending;
              return (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', padding: '7px 10px', marginBottom: '6px',
                  borderRadius: '6px', borderLeft: `3px solid ${accent}`,
                  backgroundColor: item.is_overdue ? '#fdf5f5' : '#fdf8ee',
                }}>
                  {/* DECLENTITY: the canonical entity under the name, so two same-name employees are
                      distinguishable here as they already are in the Tasks list.
                      A COLUMN rather than a sub-line inside the span, because this row is a FLEX row
                      and not a table cell: stacking the two inside the existing span would not have
                      put the entity beneath the name. The same 11px muted grey as everywhere else.
                      Server-resolved, never derived here; absent renders nothing at all. */}
                  <span style={{ flexShrink: 0, marginRight: '8px', display: 'flex',
                                 flexDirection: 'column', minWidth: 0 }}>
                    <span style={{ fontWeight: 600, fontSize: '13px', color: C.navy }}>{item.name}</span>
                    {item.entity && (
                      <span style={{ fontSize: '11px', color: '#777', marginTop: '1px' }}>
                        {item.entity}
                      </span>
                    )}
                  </span>
                  {/* COMPSECTION: translated at render; any other task title passes through unchanged. */}
                  <span style={{ flex: 1, fontSize: '13px', color: '#555', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{complianceTaskTitle(item.description, t)}</span>
                  <span style={{ fontSize: '12px', fontWeight: 600, marginLeft: '8px', flexShrink: 0, color: accent }}>{item.days}d</span>
                </div>
              );
            })}
          </div>

          {/* Card B - Compliance by entity */}
          <div style={s.card}>
            <h3 style={s.cardTitle}>Compliance by entity</h3>
            {byEntity.length === 0 ? (
              <p style={s.empty}>No entity data</p>
            ) : byEntity.map(row => (
              <div key={row.entity} style={{ marginBottom: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                  <span style={{ fontSize: '12px', fontWeight: 600, color: C.navy, flex: 1, marginRight: '10px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {row.entity}
                  </span>
                  <span style={{ fontSize: '12px', fontWeight: 700, color: C.done, flexShrink: 0 }}>{row.rate}%</span>
                </div>
                <div style={{ height: '8px', backgroundColor: '#edecea', borderRadius: '4px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${row.rate}%`, backgroundColor: C.done, borderRadius: '4px', transition: 'width 0.3s ease' }} />
                </div>
              </div>
            ))}
          </div>

        </div>
      </div>
      </>)}
    </div>
  );
}

const REG_STATUS_STYLE = {
  Approved: { backgroundColor: '#eaf3ee', color: '#2D4A3E' },
  Pending:  { backgroundColor: '#fdf8ee', color: '#7a4d00' },
  Rejected: { backgroundColor: '#fdf5f5', color: '#7a1f1f' },
};

// REGFULL: every field the employee filled in, plus the shared columns compliance asked for:
// the submission date, the date of the underlying event, the reporting delay in calendar days,
// who submitted it (employee or delegate) and the compliance decision with who made it and when.
//
// THE SHARED COLUMNS ARE DEFINED ONCE and spread into each register, so the four cannot drift
// apart on either their wording or their order. Each register reads: WHO, WHEN (event,
// submission, delay), WHAT was declared, then the DECISION.
//
// NO RESTRICTED PERSONAL FIELD APPEARS HERE and none is served: NI number, mobile, personal
// email, personal phone, signature and photo are not columns of any declaration model, are not
// built into any register row, and a test asserts they reach neither the screen nor any export.
const REG_HEAD = [
  { k: 'employee_name', h: 'Employee' },
  { k: 'entity', h: 'Entity' },
  { k: 'submitted_by_display', h: 'Submitted by' },
];

const REG_TIME = [
  { k: 'event_date', h: 'Date of event' },
  { k: 'submitted_on', h: 'Submission date' },
  // Blank when it cannot be computed honestly: no event date, or an event dated after its
  // submission (a pre-declared trade). The server sends null and the cell stays empty rather
  // than showing 0, which would read as "reported same day".
  { k: 'reporting_delay_days', h: 'Delay (days)' },
];

const REG_TAIL = [
  { k: 'status', h: 'Status' },
  { k: 'review_decision', h: 'Decision' },
  { k: 'reviewed_by', h: 'Decided by' },
  { k: 'reviewed_at', h: 'Decision date' },
  { k: 'review_comments', h: 'Comments' },
];

const REGISTERS = [
  { key: 'outside-affiliations', label: 'Outside Affiliations', file: 'outside_affiliations',
    columns: [
      ...REG_HEAD, ...REG_TIME,
      { k: 'affiliated_entity', h: 'Affiliated entity' },
      { k: 'nature_activities', h: 'Nature of activities' },
      { k: 'position', h: 'Position' },
      { k: 'position_details', h: 'Position details' },
      { k: 'is_competitor', h: 'Competitor', bool: true },
      { k: 'is_paid_employment', h: 'Paid', bool: true },
      { k: 'is_none_of_above', h: 'None of the above', bool: true },
      { k: 'date_commenced', h: 'Commenced' },
      { k: 'date_ceased', h: 'Ceased' },
      { k: 'additional_information', h: 'Additional information' },
      ...REG_TAIL,
    ] },
  { key: 'gifts', label: 'Gifts & Entertainment', file: 'gifts_entertainment',
    columns: [
      ...REG_HEAD, ...REG_TIME,
      { k: 'entry_type', h: 'Gift or entertainment' },
      { k: 'date', h: 'Date' },
      { k: 'nature', h: 'Description' },
      // GEHLABEL: the LABEL only, the key is still `purpose`.
      { k: 'purpose', h: 'Detail of GE&H' },
      { k: 'benefit_to_fund', h: 'Benefit to underlying fund/investors' },
      { k: 'firm_name', h: 'Firm' },
      { k: 'individual_contact', h: 'Individual contact' },
      { k: 'firm_relationship', h: 'Relationship with the firm' },
      { k: 'firm_relationship_other', h: 'Relationship (other)' },
      { k: 'amount', h: 'Value', money: true },
      { k: 'threshold_status', h: 'Threshold' },
      ...REG_TAIL,
    ] },
  { key: 'pad', label: 'Personal Account Dealing', file: 'personal_account_dealing',
    columns: [
      ...REG_HEAD, ...REG_TIME,
      // The field compliance specifically asked for: on whose behalf the trade was made.
      { k: 'trade_for', h: 'Traded on behalf of' },
      { k: 'close_link_details', h: 'Close link details' },
      { k: 'security', h: 'Security' },
      { k: 'trade_type', h: 'Security type' },
      { k: 'direction', h: 'Buy or sell' },
      { k: 'trade_date', h: 'Trade date' },
      { k: 'price', h: 'Price', money: true },
      { k: 'quantity', h: 'Quantity' },
      { k: 'pre_approval_type', h: 'Pre-approval type' },
      { k: 'unlisted_attestation', h: 'Unlisted attestation' },
      // REGFULL: the attachments, as PORTAL links. `attach` names the slot, and the cell renders
      // a RegisterAttachment which fetches the bytes with the caller's bearer token, exactly as
      // PadAttachmentLink does on the declaration detail. No SharePoint URL is involved and none
      // is sent to the browser.
      { k: 'pre_approval_file_name', h: 'Pre-approval', attach: 'preapproval' },
      { k: 'confirmation_file_name', h: 'Trade confirmation', attach: 'confirmation' },
      ...REG_TAIL,
    ] },
  { key: 'public-officials', label: 'Public Officials', file: 'public_officials',
    columns: [
      ...REG_HEAD, ...REG_TIME,
      { k: 'official_names', h: 'Official name' },
      { k: 'position_link', h: 'Position and link' },
      { k: 'means_of_engagement', h: 'Means of engagement' },
      { k: 'engagement_date', h: 'Date of interaction' },
      { k: 'location', h: 'Location' },
      { k: 'purpose', h: 'Purpose' },
      ...REG_TAIL,
    ] },
  // ATTESTREGISTER: the fifth register. Attestations were the one declaration type with no
  // all-employees view and no CSV export. Adding the entry is the whole frontend change - the
  // generic RegistersView below already renders and exports any register in this list, so no new
  // component was needed.
  //
  // This lists one row per ATTESTATION RECORD, so only employees who have at least one appear.
  // That is deliberate and is the difference from the Overview tab's attestations-by-type, which
  // walks every employee and reports a status for each.
  //
  // REGFULL deliberately leaves this register's columns ALONE: an attestation has no form fields,
  // no underlying event date and no reporting delay (it is a periodic confirmation, not a report
  // of an event), so the shared columns would all be empty. ATTESTSELF also means it has no
  // approval, so there is no decision to show.
  { key: 'attestations', label: 'Attestations', file: 'attestations',
    columns: [
      { k: 'employee_name', h: 'Employee' },
      { k: 'entity', h: 'Entity' },
      { k: 'attestation_type', h: 'Type' },
      { k: 'period', h: 'Period' },
      { k: 'attested_at', h: 'Attested' },
      { k: 'status', h: 'Status' },
    ] },
];

// ATTESTSTATUS: the status filter options, PER REGISTER.
//
// STATUS_OPTS stays the DEFAULT and is byte-unchanged, because it is correct for the four
// declaration registers: Outside Affiliations, Gifts & Entertainment, Personal Account Dealing and
// Public Officials all really do carry Pending / Approved / Rejected, which a compliance officer sets
// when reviewing a declaration. Verified against the dev data before changing anything (outside
// affiliations held Pending, Approved and Rejected rows; PAD held all three), so editing this shared
// constant would have broken four working filters to fix one.
//
// ATTESTATIONS ARE DIFFERENT, and this is the defect. An attestation is not reviewed or approved by
// anyone: the employee either makes it or has not yet. Attestation.status is documented in the model
// as "pending / completed" and the dev data holds exactly those two values and no others. So the
// register offered Approved and Rejected, which could never match a single row, and offered NO WAY AT
// ALL to see the completed ones. There were 7 completed attestations in dev that no filter could
// reach.
//
// A PER-REGISTER OVERRIDE rather than a per-register `statusOptions` field on every REGISTERS entry:
// four of the five would carry an identical value, and a reader would have to compare five lists to
// learn that only one differs. One keyed exception states the exception.
const STATUS_OPTS = ['All', 'Pending', 'Approved', 'Rejected'];
const STATUS_OPTS_BY_REGISTER = {
  attestations: ['All', 'Pending', 'Completed'],
};
const statusOptsFor = (key) => STATUS_OPTS_BY_REGISTER[key] || STATUS_OPTS;

// REGFULL: a register attachment, served THROUGH THE PORTAL.
//
// THE DEFECT: the compliance officer has no direct access to the Compliance SharePoint site, so a
// link that pointed there failed for her even though the portal was showing her the row. The PAD
// detail panel was fixed for this (PADATTACH / PadAttachmentLink) but the REGISTERS never were.
//
// This is that same pattern, addressed by record id plus slot: the bytes are fetched with axios,
// which carries the Authorization header, and handed to the browser as a blob URL. The server
// re-checks the caller's role AND entity scope on every request, so a link is only as powerful as
// the account clicking it. No SharePoint URL exists anywhere in this component.
function RegisterAttachment({ recordId, slot, name }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState(null);

  if (!name) return <span style={{ color: '#ccc' }}>-</span>;

  const open = async () => {
    setBusy(true); setErr(null);
    try {
      const res = await axios.get(
        `${API}/compliance/pad/${recordId}/attachment/${slot}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      window.open(url, '_blank', 'noopener');
      // Revoked on a timer rather than immediately: revoking synchronously can race the new tab
      // before it has read the blob.
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      // PADPREVIEW: the server's own sentence, read through the shared helper. With
      // responseType 'blob' the error body is a Blob, so `e.response.data.detail` was always
      // undefined here and only the fallback was ever shown. The message stays inside this panel.
      setErr(await blobErrorDetail(e, 'Could not open this attachment.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <span>
      <button type="button" onClick={open} disabled={busy} style={s.fileBtn} title={name}>
        {busy ? 'Opening...' : name}
      </button>
      {err && <div style={s.fileErr}>{err}</div>}
    </span>
  );
}

function RegistersView() {
  const [data, setData]       = useState({});
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(true);
  const [err, setErr]         = useState(null);

  useEffect(() => {
    Promise.all(REGISTERS.map(r =>
      axios.get(`${API}/compliance/registers/${r.key}`).then(res => [r.key, res.data])
    ))
      .then(pairs => setData(Object.fromEntries(pairs)))
      .catch(() => setErr('Failed to load registers.'))
      .finally(() => setLoading(false));
  }, []);

  const getFilter = (key) => filters[key] || { status: 'All', entity: 'All' };
  const setFilter = (key, patch) => setFilters(f => ({ ...f, [key]: { ...getFilter(key), ...patch } }));

  const exportCsv = async (reg) => {
    try {
      const res = await axios.get(`${API}/compliance/registers/${reg.key}/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url; a.download = `${reg.file}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { /* download failed - ignore */ }
  };

  if (loading) return <p style={s.empty}>Loading registers...</p>;
  if (err) return <p style={{ ...s.empty, color: C.overdue }}>{err}</p>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', paddingBottom: '24px' }}>
      {REGISTERS.map(reg => {
        const rows = data[reg.key] || [];
        const f = getFilter(reg.key);
        const entities = ['All', ...Array.from(new Set(rows.map(r => r.entity).filter(Boolean)))];
        const filtered = rows.filter(r =>
          (f.status === 'All' || (r.status || '').toLowerCase() === f.status.toLowerCase()) &&
          (f.entity === 'All' || r.entity === f.entity)
        );
        return (
          <div key={reg.key} style={s.card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '10px' }}>
              <h3 style={{ ...s.cardTitle, margin: 0 }}>
                {reg.label} <span style={{ color: '#aaa', fontWeight: 400 }}>({filtered.length})</span>
              </h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <select value={f.status} onChange={e => setFilter(reg.key, { status: e.target.value })} style={s.select}>
                  {statusOptsFor(reg.key).map(o => <option key={o} value={o}>{o === 'All' ? 'All statuses' : o}</option>)}
                </select>
                <select value={f.entity} onChange={e => setFilter(reg.key, { entity: e.target.value })} style={s.select}>
                  {entities.map(o => <option key={o} value={o}>{o === 'All' ? 'All entities' : o}</option>)}
                </select>
                <button onClick={() => exportCsv(reg)} style={s.exportBtn}>Export CSV</button>
              </div>
            </div>
            {filtered.length === 0 ? (
              <p style={s.empty}>No submissions</p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>{reg.columns.map(c => <th key={c.k} style={s.th}>{c.h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {filtered.map(row => (
                      <tr key={row.id} style={{ borderBottom: '0.5px solid #f4f4f4' }}>
                        {reg.columns.map(c => (
                          <td key={c.k} style={s.td}>
                            {c.k === 'status'
                              ? <span style={{ ...s.badge, ...(REG_STATUS_STYLE[row.status] || {}) }}>{row.status || '-'}</span>
                              : c.attach
                                // REGFULL: through the portal, never to SharePoint.
                                ? <RegisterAttachment recordId={row.id} slot={c.attach} name={row[c.k]} />
                                : c.money
                                  ? (row[c.k] != null && row[c.k] !== '' ? `${row.currency ? row.currency + ' ' : ''}${row[c.k]}` : '-')
                                  : c.bool
                                    // A Yes/No question on the form reads as Yes/No here, not as
                                    // true/false, and an unanswered one stays blank.
                                    ? (row[c.k] == null ? '-' : (row[c.k] ? 'Yes' : 'No'))
                                    // REGFULL: null and empty string both render as a dash. This
                                    // is what carries the reporting-delay rule on screen, and 0
                                    // must NOT be swallowed - a same-day report shows 0.
                                    : (row[c.k] === 0 ? '0' : (row[c.k] || '-'))}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

const s = {
  card:      { backgroundColor: '#fff', borderRadius: '10px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '0.5px solid #EEE' },
  cardTitle: { fontSize: '14px', fontWeight: 600, color: '#1a2744', margin: '0 0 14px 0' },
  badge:     { borderRadius: '10px', padding: '2px 8px', fontSize: '11px', fontWeight: 600, flexShrink: 0, whiteSpace: 'nowrap' },
  empty:     { fontSize: '13px', color: '#667085', fontStyle: 'italic', margin: '8px 0 0 0' },
  select:    { fontSize: '12px', padding: '5px 8px', border: '1px solid #ddd', borderRadius: '6px', background: '#fff', color: '#333', cursor: 'pointer' },
  exportBtn: { fontSize: '12px', fontWeight: 600, padding: '6px 14px', border: 'none', borderRadius: '6px', backgroundColor: '#1a2744', color: '#fff', cursor: 'pointer' },
  th:        { textAlign: 'left', fontSize: '11px', fontWeight: 600, color: '#aaa', padding: '6px 10px', borderBottom: '0.5px solid #eee', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' },
  td:        { padding: '9px 10px', fontSize: '13px', color: '#333', whiteSpace: 'nowrap' },
  fileBtn:   { background: 'none', border: 'none', padding: 0, font: 'inherit', color: '#1a2744',
               textDecoration: 'underline', cursor: 'pointer', maxWidth: '180px',
               overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
               display: 'inline-block', verticalAlign: 'bottom' },
  fileErr:   { fontSize: '11px', color: '#7a1f1f', whiteSpace: 'normal', maxWidth: '220px' },
};